import { useState, useEffect } from 'react';
import { Check, FileText, AlertCircle, HelpCircle, Bug, Lightbulb, Settings } from 'lucide-react';
import { clsx } from 'clsx';
import type { TicketType } from '@centraoneplus/shared-types';
import { ticketTypesService } from '@services/ticket-types.service';
import { ThemedSelect } from '@components/common/ThemedSelect';

interface TicketTypeSelectorProps {
  value?: string;
  onChange: (ticketType: TicketType | null) => void;
  disabled?: boolean;
  error?: string;
  required?: boolean;
  className?: string;
  /**
   * Pick the type marked default on load (the original behaviour). The create
   * page's picker step turns this off: there, selecting a type navigates to the
   * details form, so an automatic pick would skip the picker entirely.
   */
  autoSelectDefault?: boolean;
}

// Map of icon names to Lucide components
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  'file-text': FileText,
  'alert-circle': AlertCircle,
  'help-circle': HelpCircle,
  'bug': Bug,
  'lightbulb': Lightbulb,
  'settings': Settings,
};

function getIconComponent(iconName?: string) {
  if (!iconName) return FileText;
  return ICON_MAP[iconName] || FileText;
}

export function TicketTypeSelector({
  value,
  onChange,
  disabled,
  error,
  required,
  className,
  autoSelectDefault = true,
}: TicketTypeSelectorProps) {
  const [ticketTypes, setTicketTypes] = useState<TicketType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    loadTicketTypes();
  }, []);

  const loadTicketTypes = async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      const types = await ticketTypesService.getTicketTypes({ active: true });
      setTicketTypes(types);

      // Only auto-select if there's an explicitly marked default ticket type
      // Otherwise, leave unselected - user can create ticket without a specific type
      if (autoSelectDefault && !value && types.length > 0) {
        const defaultType = types.find((t) => t.isDefault);
        if (defaultType) {
          onChange(defaultType);
        }
        // If no default, don't auto-select - the basic form will be shown
      }
    } catch (err: any) {
      console.error('Failed to load ticket types:', err);
      setLoadError(err.message || 'Failed to load ticket types');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = (ticketType: TicketType) => {
    if (disabled) return;
    // Allow deselecting by clicking the same type again
    if (value === ticketType.id) {
      onChange(null);
    } else {
      onChange(ticketType);
    }
  };

  if (isLoading) {
    return (
      <div className={clsx('animate-pulse', className)}>
        <div className="h-4 w-24 bg-gray-200 rounded mb-2 dark:bg-gray-700"></div>
        <div className="flex gap-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 w-32 bg-gray-200 rounded-lg dark:bg-gray-700"></div>
          ))}
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={clsx('p-4 bg-red-50 rounded-lg', className)}>
        <p className="text-sm text-red-600">{loadError}</p>
        <button
          type="button"
          onClick={loadTicketTypes}
          className="mt-2 text-sm text-red-700 underline hover:no-underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (ticketTypes.length === 0) {
    return (
      <div className={clsx('p-4 bg-gray-50 dark:bg-gray-800 rounded-lg', className)}>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          No ticket types configured. You can still create a general ticket.
        </p>
      </div>
    );
  }

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Ticket Type{' '}
        {required ? (
          <span className="text-red-500">*</span>
        ) : (
          <span className="text-gray-400 font-normal dark:text-gray-500">(optional)</span>
        )}
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {ticketTypes.map((ticketType) => {
          const isSelected = value === ticketType.id;
          const Icon = getIconComponent(ticketType.icon);

          return (
            <button
              key={ticketType.id}
              type="button"
              onClick={() => handleSelect(ticketType)}
              disabled={disabled}
              title={ticketType.description || ticketType.name}
              className={clsx(
                'relative flex flex-col items-start text-left h-full p-4 rounded-xl border-2 transition-all',
                isSelected
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm bg-white dark:bg-gray-800',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
              style={{
                borderColor: isSelected && ticketType.color ? ticketType.color : undefined,
                backgroundColor: isSelected && ticketType.color ? `${ticketType.color}10` : undefined,
              }}
            >
              {isSelected && (
                <div
                  className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: ticketType.color || '#3b82f6' }}
                >
                  <Check className="w-3 h-3 text-white" />
                </div>
              )}
              <div className="flex items-center gap-2.5 mb-1.5 pr-6">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: ticketType.color ? `${ticketType.color}20` : '#e5e7eb',
                    color: ticketType.color || '#6b7280',
                  }}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {ticketType.name}
                </span>
              </div>
              {ticketType.description && (
                <span className="text-xs leading-relaxed text-gray-500 dark:text-gray-400 line-clamp-2">
                  {ticketType.description}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
}

interface SelectedTicketTypeCardProps {
  ticketType: TicketType;
  onChange: () => void;
  disabled?: boolean;
}

/**
 * One-line summary of the type chosen on the create page's picker step, shown
 * above the details form in place of the full grid. "Change" goes back to the
 * picker.
 */
export function SelectedTicketTypeCard({ ticketType, onChange, disabled }: SelectedTicketTypeCardProps) {
  const Icon = getIconComponent(ticketType.icon);
  return (
    <div className="flex items-center gap-3">
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{
          backgroundColor: ticketType.color ? `${ticketType.color}20` : '#e5e7eb',
          color: ticketType.color || '#6b7280',
        }}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Ticket Type</p>
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{ticketType.name}</p>
        {ticketType.description && (
          <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">{ticketType.description}</p>
        )}
      </div>
      <button type="button" onClick={onChange} disabled={disabled} className="btn-secondary whitespace-nowrap">
        Change
      </button>
    </div>
  );
}

// Compact dropdown version for use in forms and sidebars
interface TicketTypeDropdownProps {
  value?: string;
  onChange: (ticketTypeId: string | undefined) => void;
  disabled?: boolean;
  error?: string;
  className?: string;
  placeholder?: string;
  showLabel?: boolean;
  autoSelect?: boolean;
  /**
   * Name of the type `value` refers to, when the caller knows it. Only active
   * types are listed, so a ticket whose type was since switched off has no
   * matching option and the control used to show the raw id instead.
   */
  currentName?: string;
}

export function TicketTypeDropdown({
  value,
  onChange,
  disabled,
  error,
  className,
  placeholder = 'Select ticket type...',
  showLabel = true,
  autoSelect = true,
  currentName,
}: TicketTypeDropdownProps) {
  const [ticketTypes, setTicketTypes] = useState<TicketType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // The ticket's own type is not among the selectable (active) ones.
  const valueIsUnlisted = !!value && !isLoading && !ticketTypes.some((t) => t.id === value);

  useEffect(() => {
    loadTicketTypes();
  }, []);

  const loadTicketTypes = async () => {
    try {
      setIsLoading(true);
      const types = await ticketTypesService.getTicketTypes({ active: true });
      setTicketTypes(types);

      // Only auto-select if enabled and there's an explicitly marked default ticket type
      if (autoSelect && !value && types.length > 0) {
        const defaultType = types.find((t) => t.isDefault);
        if (defaultType) {
          onChange(defaultType.id);
        }
      }
    } catch (err) {
      console.error('Failed to load ticket types:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={className}>
      {showLabel && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Ticket Type
        </label>
      )}
      <ThemedSelect
        value={value || ''}
        onChange={(e) => onChange(e.target.value || undefined)}
        disabled={disabled || isLoading}
        className={clsx(
          'block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1',
          error
            ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
            : 'border-gray-300 dark:border-gray-600 focus:border-blue-500 focus:ring-blue-500',
          'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100'
        )}
      >
        {isLoading ? (
          <option value="">Loading...</option>
        ) : ticketTypes.length === 0 && !valueIsUnlisted ? (
          <option value="">No ticket types available</option>
        ) : (
          <>
            <option value="">{placeholder}</option>
            {valueIsUnlisted && (
              <option value={value}>{currentName ? `${currentName} (inactive)` : 'Inactive ticket type'}</option>
            )}
            {ticketTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </>
        )}
      </ThemedSelect>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}
